/* The gate review sheet (designs/products-pdp.md PP7/PP8 + UI "Gate review
   sheet (Screen 5)"): the approver sees exactly what the certificate will
   freeze — the phase's bar and every owed row, not recorded first — and
   records passed / passed with conditions / held. Passing with gaps is
   allowed; the consequence line says, live, what the save writes. Pure: the
   owner opens it with `reviewDraft`, POSTs `GateReviewInput`, hands the
   server's refusal back through `error` (a non-Admin's 403 lands there), and
   returns focus to #record-review on Cancel/save. `readOnly` is the
   open-to-anyone view with no Record button (the UX artifact's "anyone can
   open the review sheet read-only"); the design's default is `false`. */
import { useEffect, useRef, useState, type JSX, type ReactNode } from "react";
import { PDP_CRITERION_AREAS, type PdpCriterion, type PdpGate, type PdpPhase } from "../../../../shared/pdp";
import { gateReviewShapeReason } from "../../../../shared/product-pdp";
import { DECISION_LABEL, fmtDate, fmtDateTime, type Decision, type GateReviewInput, type PhaseBar, type SlotCounts, type SlotState } from "./model";
import { PhaseBars } from "./PhaseBars";
import { StateChip } from "./StateChip";

export interface ReviewRow {
  key: string;
  name: string;
  state: SlotState;
  percent?: number;
}

export interface GateReviewSheetProps {
  productName: string;
  phase: PdpPhase;
  /* the product's declared phase — equal to `phase` for the ordinary sheet;
     later when the sheet corrects an earlier gate (PP10), which changes what
     a decision does to the phase */
  declaredPhase?: PdpPhase;
  /* phaseBars()'s entry for this phase */
  bar: PhaseBar;
  /* every owed row the certificate will freeze (phases 0..this one), any order */
  rows: ReviewRow[];
  /* the counts the consequence line reads — over every owed row, the set the
     server stamps as open gaps (PP7); defaults to the bar's */
  gapCounts?: SlotCounts;
  /* the phase's exit criteria from the catalog (UC step 2) */
  criteria?: PdpCriterion[];
  /* by criterion id: the linked feed slot's reading line, as the owner draws
     it (kpi-feed-seam.md KF13) — what the certificate will freeze */
  criterionLines?: Record<string, ReactNode>;
  /* the catalog gates whose afterPhase is this phase (PC8: normally one) */
  gates: PdpGate[];
  /* the gate's approver role name; null = not recorded (red) */
  approverRoleName: string | null;
  /* earlier, not-superseded reviews of this gate */
  earlierReviews: { id: string; decision: Decision; reviewDate: string }[];
  /* ISO instant for "as of" */
  now: string;
  /* YYYY-MM-DD: the date input's default and max */
  today: string;
  error: string | null;
  /* counts the owner's refusals, so the same sentence twice re-focuses */
  errorNonce?: number;
  saving?: boolean;
  readOnly?: boolean;
  onSave: (input: GateReviewInput) => void;
  onCancel: () => void;
}

const STATE_ORDER: SlotState[] = ["missing", "unconfirmed", "estimated", "evidence"];
const FOLD_AFTER = 5;
const FOLD_LABEL: Record<SlotState, string> = { missing: "not recorded", unconfirmed: "unconfirmed", estimated: "estimated", evidence: "evidenced" };
const TEXT_MAX = 2000;

export const orderRows = (rows: ReviewRow[]): ReviewRow[] =>
  STATE_ORDER.flatMap((state) => rows.filter((r) => r.state === state));

/* PP8 read out loud, live: the stamped gaps, the advance (none after Phase 6)
   and the certificate; held leaves the phase where it is */
/* what the server will do to the phase (PP8/PP10), said before the save:
   a pass advances only while the product still stands at this gate's phase;
   a held review superseding a pass after the phase moved on pulls it back */
export function consequenceLine(
  decision: Decision,
  phase: PdpPhase,
  counts: SlotCounts,
  reviewDate: string,
  declaredPhase: PdpPhase = phase,
  supersededDecision: Decision | null = null,
): string {
  const gaps = `On save: ${counts.missing} not recorded and ${counts.unconfirmed} unconfirmed rows are written into this record as open gaps;`;
  if (decision === "held") {
    const pullsBack = supersededDecision !== null && supersededDecision !== "held" && declaredPhase !== phase;
    return pullsBack ? `${gaps} the phase goes back to Phase ${phase}.` : `${gaps} the phase stays at Phase ${declaredPhase}.`;
  }
  const effective = reviewDate ? fmtDate(reviewDate) : "the review date";
  const advance =
    declaredPhase !== phase ? ` the phase stays at Phase ${declaredPhase};` : phase < 6 ? ` the phase advances to Phase ${phase + 1}, effective ${effective};` : "";
  return `${gaps}${advance} a Phase ${phase} certificate is issued.`;
}

/* PP7's shape rules said before the round trip — the SHARED rule, so the
   sheet and the server never disagree; the gate/phase/product rules need
   the record and stay on the server (they arrive through `error`) */
export function gateReviewFormReason(input: GateReviewInput, today: string): string | null {
  if (!input.gateId) return "pick the gate this review decides";
  return gateReviewShapeReason(input, today);
}

function FormError({ error, nonce }: { error: string | null; nonce: number }): JSX.Element {
  const ref = useRef<HTMLParagraphElement>(null);
  useEffect(() => {
    if (error) ref.current?.focus();
  }, [error, nonce]);
  return (
    <p className="form-error" role="alert" tabIndex={-1} ref={ref}>
      {error ?? ""}
    </p>
  );
}

/* one <tbody> per state; rows past the fold are hidden until the fold row's
   button opens them (a <details> cannot sit inside a table) */
function RowsTable({ rows }: { rows: ReviewRow[] }): JSX.Element {
  const [open, setOpen] = useState<SlotState[]>([]);
  return (
    <table className="review-rows">
      <thead>
        <tr>
          <th>Owed row</th>
          <th>State</th>
        </tr>
      </thead>
      {STATE_ORDER.map((state) => {
        const group = rows.filter((r) => r.state === state);
        if (group.length === 0) return null;
        const folded = group.length - FOLD_AFTER;
        const isOpen = open.includes(state);
        return (
          <tbody key={state} data-state={state}>
            {group.map((r, i) => (
              <tr className="review-row" key={r.key} hidden={i >= FOLD_AFTER && !isOpen}>
                <td>{r.name}</td>
                <td>
                  <StateChip state={r.state} percent={r.percent} />
                </td>
              </tr>
            ))}
            {folded > 0 ? (
              <tr className="review-fold">
                <td colSpan={2}>
                  <button
                    type="button"
                    className="btn link"
                    aria-expanded={isOpen}
                    onClick={() => setOpen((o) => (isOpen ? o.filter((s) => s !== state) : [...o, state]))}
                  >
                    {isOpen ? `hide ${folded} ${FOLD_LABEL[state]}` : `… ${folded} more ${FOLD_LABEL[state]}`}
                  </button>
                </td>
              </tr>
            ) : null}
          </tbody>
        );
      })}
    </table>
  );
}

export function GateReviewSheet(props: GateReviewSheetProps): JSX.Element {
  const { productName, phase, declaredPhase = phase, bar, rows, gapCounts = bar.counts, criteria = [], criterionLines = {}, gates, approverRoleName, earlierReviews, now, today, error, errorNonce = 0, saving, readOnly, onSave, onCancel } = props;
  const inAreaOrder = [...criteria].sort((a, b) => PDP_CRITERION_AREAS.indexOf(a.area) - PDP_CRITERION_AREAS.indexOf(b.area));
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    heading.current?.focus();
  }, []);
  const [gateId, setGateId] = useState(gates.length === 1 ? gates[0]!.id : "");
  const [decision, setDecision] = useState<Decision>("passed");
  const [text, setText] = useState("");
  const [reviewDate, setReviewDate] = useState(today);
  const [minutes, setMinutes] = useState("");
  const [supersedes, setSupersedes] = useState("");
  const [why, setWhy] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);
  const [localNonce, setLocalNonce] = useState(0);

  const gateLabel = gates.find((g) => g.id === gateId)?.label ?? gates[0]?.label ?? `Phase ${phase} exit`;
  const build = (): GateReviewInput => ({
    gateId,
    decision,
    conditions: decision === "passed-with-conditions" ? text.trim() || null : null,
    reason: decision === "held" ? text.trim() || null : supersedes && why.trim() ? why.trim() : null,
    reviewDate,
    minutesUrl: minutes.trim() || null,
    supersedes: supersedes || null,
  });
  const submit = (): void => {
    const input = build();
    const refused = gateReviewFormReason(input, today);
    if (refused) {
      setLocalError(refused);
      setLocalNonce((n) => n + 1);
      return;
    }
    setLocalError(null);
    onSave(input);
  };
  const dec = (value: Decision, id: string, label: string): JSX.Element => (
    <span className="seg-item" key={id}>
      <input
        type="radio"
        name="decision"
        id={id}
        checked={decision === value}
        disabled={readOnly}
        onChange={() => {
          setDecision(value);
          setLocalError(null);
        }}
      />
      <label htmlFor={id}>{label}</label>
    </span>
  );

  return (
    <section id="review-sheet" className="area-panel review-sheet">
      <h3 tabIndex={-1} ref={heading}>
        {gateLabel} review · {productName} · Phase {phase} exit
      </h3>
      <div className="review-cols">
        <div className="review-left">
          <h4>What is in place as of {fmtDateTime(now)}</h4>
          <PhaseBars bars={[bar]} />
          {inAreaOrder.length > 0 ? (
            <ul className="slots pdp-criteria review-criteria" aria-label={`Exit criteria · Phase ${phase}`}>
              {inAreaOrder.map((c) => (
                <li key={c.id}>
                  <div className="slot-name">{c.area}</div>
                  <div className="val">
                    {c.text ?? <span className="state missing">not recorded</span>}
                    {c.unconfirmed ? <span className="state unconfirmed">unconfirmed</span> : null}
                  </div>
                  {criterionLines[c.id]}
                </li>
              ))}
            </ul>
          ) : null}
          <RowsTable rows={orderRows(rows)} />
        </div>
        <form
          id="review-form"
          className="review-right card-form"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <h4>Decision</h4>
          <p className="slot-note review-approver">
            approver role: {approverRoleName ?? <span className="state missing">not recorded</span>} · recording gated on Admin
            {readOnly ? " — read-only" : ""}
          </p>
          {gates.length > 1 ? (
            <label className="field" htmlFor="review-gate">
              <span className="field-label">Gate</span>
              <select id="review-gate" value={gateId} disabled={readOnly} onChange={(e) => setGateId(e.target.value)}>
                <option value="">— choose —</option>
                {gates.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.label}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <p className="review-gate-fixed">{gateLabel}</p>
          )}
          <fieldset className="seg">
            <legend>Outcome</legend>
            {dec("passed", "dec-passed", "Passed")}
            {dec("passed-with-conditions", "dec-conditions", "Passed with conditions")}
            {dec("held", "dec-held", "Held")}
          </fieldset>
          {decision !== "passed" ? (
            <label className="field" htmlFor="review-conditions">
              <span className="field-label">{decision === "held" ? "Reason for hold" : "Conditions"}</span>
              <textarea id="review-conditions" rows={3} maxLength={TEXT_MAX} value={text} disabled={readOnly} onChange={(e) => setText(e.target.value)} />
            </label>
          ) : null}
          <label className="field" htmlFor="review-date">
            <span className="field-label">Review date</span>
            <input type="date" id="review-date" value={reviewDate} max={today} disabled={readOnly} onChange={(e) => setReviewDate(e.target.value)} />
          </label>
          <label className="field" htmlFor="review-minutes">
            <span className="field-label">Minutes or deck</span>
            <input type="url" id="review-minutes" value={minutes} disabled={readOnly} onChange={(e) => setMinutes(e.target.value)} />
          </label>
          {earlierReviews.length ? (
            <label className="field" htmlFor="review-supersedes">
              <span className="field-label">Supersedes</span>
              <select id="review-supersedes" value={supersedes} disabled={readOnly} onChange={(e) => setSupersedes(e.target.value)}>
                <option value="">— none —</option>
                {earlierReviews.map((r) => (
                  <option key={r.id} value={r.id}>
                    {DECISION_LABEL[r.decision]} · {fmtDate(r.reviewDate)}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          {supersedes && decision !== "held" ? (
            <label className="field" htmlFor="supersede-reason">
              <span className="field-label">Why this review supersedes it</span>
              <input type="text" id="supersede-reason" value={why} maxLength={TEXT_MAX} disabled={readOnly} onChange={(e) => setWhy(e.target.value)} />
            </label>
          ) : null}
          <p className="review-consequence" id="review-consequence">
            {consequenceLine(decision, phase, gapCounts, reviewDate, declaredPhase, earlierReviews.find((r) => r.id === supersedes)?.decision ?? null)}
          </p>
          <FormError error={localError ?? error} nonce={localNonce + errorNonce} />
          <div className="form-actions">
            {readOnly ? null : (
              <button type="submit" id="record-decision" className="btn primary" disabled={saving}>
                Record decision
              </button>
            )}
            <button type="button" id="cancel-review" className="btn ghost" onClick={onCancel}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
